import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  XMarkIcon,
  ClockIcon,
  UserIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { adminApiService } from '../../../../shared/services/adminApi'
import toast from 'react-hot-toast'
import { formatDateTime as formatLocaleDateTime } from '../../../../shared/lib/format'

interface PlanHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  userEmail: string
}

interface PlanHistoryItem {
  id: string
  oldPlan: string
  newPlan: string
  reason: string
  method: string
  changedAt: string
  changedBy: {
    email: string
    name?: string
  } | null
  metadata: any
}

interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalCount: number
  hasNext: boolean
  hasPrev: boolean
}

export default function PlanHistoryModal({
  isOpen,
  onClose,
  userId,
  userEmail,
}: PlanHistoryModalProps) {
  const [planHistory, setPlanHistory] = useState<PlanHistoryItem[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (isOpen && userId) {
      loadPlanHistory()
    }
  }, [isOpen, userId, currentPage])

  const loadPlanHistory = async () => {
    try {
      setLoading(true)
      const data = await adminApiService.getUserPlanHistory(userId, {
        page: currentPage,
        limit: 20,
      })

      setPlanHistory(data.planHistory || [])
      setPagination(data.pagination)
      setCurrentUser(data.user)
    } catch (error) {
      console.error('Failed to load plan history:', error)
      toast.error('Failed to load plan history')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return formatLocaleDateTime(dateString)
  }

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'free':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      case 'growth':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'scale':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      case 'enterprise':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'admin_manual':
        return <CogIcon className="h-4 w-4" />
      case 'subscription':
        return <UserIcon className="h-4 w-4" />
      case 'upgrade':
        return <ArrowRightIcon className="h-4 w-4" />
      default:
        return <DocumentTextIcon className="h-4 w-4" />
    }
  }

  const getMethodDisplay = (method: string) => {
    switch (method) {
      case 'admin_manual':
        return 'Admin Change'
      case 'subscription':
        return 'Subscription'
      case 'upgrade':
        return 'User Upgrade'
      case 'downgrade':
        return 'User Downgrade'
      default:
        return method.charAt(0).toUpperCase() + method.slice(1)
    }
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Plan Change History
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currentUser?.email || userEmail}
              {currentUser?.currentPlan && (
                <span className="ml-2">
                  Current Plan:
                  <span
                    className={`ml-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPlanColor(currentUser.currentPlan)}`}
                  >
                    {currentUser.currentPlan}
                  </span>
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                Loading plan history...
              </span>
            </div>
          ) : planHistory.length === 0 ? (
            <div className="py-12 text-center">
              <ClockIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                No Plan Changes
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                This user has not made any plan changes yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {planHistory.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center space-x-3">
                        {/* Plan Change Visualization */}
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPlanColor(item.oldPlan)}`}
                          >
                            {item.oldPlan}
                          </span>
                          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPlanColor(item.newPlan)}`}
                          >
                            {item.newPlan}
                          </span>
                        </div>

                        {/* Method Badge */}
                        <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                          {getMethodIcon(item.method)}
                          <span>{getMethodDisplay(item.method)}</span>
                        </div>
                      </div>

                      {/* Reason */}
                      {item.reason && (
                        <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
                          <strong>Reason:</strong> {item.reason}
                        </p>
                      )}

                      {/* Changed By */}
                      {item.changedBy && (
                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                          <strong>Changed by:</strong>{' '}
                          {item.changedBy.name || item.changedBy.email}
                        </p>
                      )}

                      {/* Metadata */}
                      {item.metadata &&
                        Object.keys(item.metadata).length > 0 && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {item.metadata.oldLimits &&
                              item.metadata.newLimits && (
                                <div className="mt-2 grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="font-medium">
                                      Previous Limits:
                                    </span>
                                    <div className="mt-1 text-xs">
                                      Links:{' '}
                                      {item.metadata.oldLimits.links === 999999
                                        ? 'Unlimited'
                                        : item.metadata.oldLimits.links?.toLocaleString()}
                                      <br />
                                      Clicks:{' '}
                                      {item.metadata.oldLimits.clicks === 999999
                                        ? 'Unlimited'
                                        : item.metadata.oldLimits.clicks?.toLocaleString()}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      New Limits:
                                    </span>
                                    <div className="mt-1 text-xs">
                                      Links:{' '}
                                      {item.metadata.newLimits.links === 999999
                                        ? 'Unlimited'
                                        : item.metadata.newLimits.links?.toLocaleString()}
                                      <br />
                                      Clicks:{' '}
                                      {item.metadata.newLimits.clicks === 999999
                                        ? 'Unlimited'
                                        : item.metadata.newLimits.clicks?.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                    </div>

                    {/* Timestamp */}
                    <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{formatDate(item.changedAt)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing page {pagination.currentPage} of {pagination.totalPages}
                <span className="ml-2">
                  ({pagination.totalCount} total changes)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
